/* Common Imports */
import { Box, Button, Link, MenuItem, Select, SelectChangeEvent, TextField, Typography } from "@mui/material";
import { styled } from "@mui/system";
import { useRouter } from "next/router";
import React, { useState } from "react";

/* Styled Components */

const CalculatorContainer = styled(Box)(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	width: "100%",
	gap: "2rem",
	padding: "4rem 12rem 4rem 12rem",
	[theme.breakpoints.down("md")]: {
		flexDirection: "column",
		padding: "2rem 1.5rem 0rem 1.5rem",
	},
}));

const MainContent = styled(Box)(({ theme }) => ({
	flex: 1,
	display: "flex",
	flexDirection: "column",
	gap: "2rem",
}));

const Sidebar = styled(Box)(({ theme }) => ({
	width: "21.875rem",
	display: "flex",
	flexDirection: "column",
	gap: "2rem",
	marginTop: "8rem",
	alignItems: "flex-start",
	[theme.breakpoints.down("md")]: {
		width: "100%",
		marginTop: 0,
	},
}));

const SidebarCard = styled(Box)(({ theme }) => ({
	backgroundColor: "#FAFAFA",
	borderRadius: "1rem",
	overflow: "hidden",
	border: "0.0625rem solid #C4C4C4",
	width: "100%",
}));

const SidebarCardHeader = styled(Box)(({ theme }) => ({
	padding: "1rem 1.5rem",
	borderBottom: "0.0625rem solid #C4C4C4",
	backgroundColor: "#FAFAFA",
}));

const SidebarCardTitle = styled(Typography)(({ theme }) => ({
	fontSize: "1.125rem",
	fontWeight: "600",
	color: "#212121",
	textAlign: "center",
}));

const SidebarCardContent = styled(Box)(({ theme }) => ({
	padding: "1rem 1.5rem",
	display: "flex",
	flexDirection: "column",
	gap: "0.75rem",
	backgroundColor: "#FFFFFF",
}));

const CalculatorLink = styled(Typography)(({ theme }) => ({
	fontSize: "1rem",
	color: "#212121",
	cursor: "pointer",
	padding: "0.5rem 0",
	"&:hover": {
		color: "#6E3037",
	},
}));

const AiBoostCard = styled(Link)(({ theme }) => ({
	position: "relative",
	backgroundImage: "url(/images/icons/ai-boost/rocket-background1.png)",
	backgroundSize: "110% 110%",
	backgroundPosition: "center",
	backgroundRepeat: "no-repeat",
	backgroundClip: "padding-box",
	borderRadius: "12px",
	border: "1px solid #8256ef",
	padding: "6rem 1.5rem 1.5rem 1.5rem",
	display: "flex",
	flexDirection: "column",
	gap: "0.75rem",
	textDecoration: "none",
	color: "inherit",
	overflow: "hidden",
}));

const AiBoostTitle = styled(Typography)(({ theme }) => ({
	fontSize: "3rem",
	fontWeight: "600",
	color: "#000000",
	textAlign: "center",
	marginTop: "2rem",
}));

const AiBoostDescription = styled(Typography)(({ theme }) => ({
	fontSize: "0.875rem",
	color: "#666666",
	lineHeight: 1.5,
	textAlign: "center",
}));

const AiBoostButton = styled(Button)(({ theme }) => ({
	backgroundColor: "#492BBD",
	color: "#FFFFFF",
	padding: "0.5rem 1rem",
	borderRadius: "0.75rem",
	textTransform: "none",
	fontSize: "1rem",
	fontWeight: "400",
	width: "100%",
	boxShadow: "none",
	"&:hover": {
		backgroundColor: "#492BBD",
		boxShadow: "none",
	},
}));

const HeaderBox = styled(Box)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	gap: "0.5rem",
	marginBottom: "1rem",
}));

const MainTitle = styled(Typography)(({ theme }) => ({
	fontSize: "2rem",
	fontWeight: "600",
	color: "#000000",
	[theme.breakpoints.down("md")]: {
		fontSize: "1.75rem",
	},
	[theme.breakpoints.down("sm")]: {
		fontSize: "1.5rem",
	},
}));

const SubTitle = styled(Typography)(({ theme }) => ({
	fontSize: "1.125rem",
	fontWeight: "400",
	color: "#666666",
	[theme.breakpoints.down("md")]: {
		fontSize: "1rem",
	},
}));

const CalculatorBox = styled(Box)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	gap: "2rem",
	backgroundColor: "#F5F5F5",
	borderRadius: "1rem",
	padding: "2rem",
	border: "0.0625rem solid #C4C4C4",
	[theme.breakpoints.down("md")]: {
		padding: "1.5rem",
	},
}));

const InputRowContainer = styled(Box)(({ theme }) => ({
	display: "grid",
	gridTemplateColumns: "1fr 1fr",
	gap: "1.5rem",
	[theme.breakpoints.down("md")]: {
		gridTemplateColumns: "1fr",
		gap: "1rem",
	},
}));

const InputWrapper = styled(Box)({
	display: "flex",
	flexDirection: "column",
	gap: "0.5rem",
});

const FieldLabel = styled(Typography)({
	fontSize: "1rem",
	fontWeight: "500",
	color: "#333333",
});

const StyledTextField = styled(TextField)(({ theme }) => ({
	"& .MuiOutlinedInput-root": {
		borderRadius: "0.75rem",
		backgroundColor: "#FFFFFF",
		fontSize: "1rem",
		"& fieldset": {
			borderColor: "#C4C4C4",
		},
		"&:hover fieldset": {
			borderColor: "#C4C4C4",
		},
		"&.Mui-focused fieldset": {
			borderColor: "#C4C4C4",
		},
	},
	"& .MuiInputBase-input": {
		padding: "1rem",
		color: "#333333",
		/* Remove number input arrows */
		"&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
			WebkitAppearance: "none",
			margin: 0,
		},
		"&[type=number]": {
			MozAppearance: "textfield",
		},
	},
}));

const StyledSelect = styled(Select)(({ theme }) => ({
	borderRadius: "0.75rem",
	backgroundColor: "#FFFFFF",
	fontSize: "1rem",
	"& .MuiOutlinedInput-notchedOutline": {
		borderColor: "#C4C4C4",
	},
	"&:hover .MuiOutlinedInput-notchedOutline": {
		borderColor: "#C4C4C4",
	},
	"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
		borderColor: "#C4C4C4",
	},
	"& .MuiSelect-select": {
		padding: "1rem",
		color: "#333333",
		backgroundColor: "#FFFFFF",
	},
	"&.Mui-focused": {
		backgroundColor: "#FFFFFF",
	},
}));

const InputWithAdornment = styled(Box)({
	position: "relative",
	display: "flex",
	alignItems: "center",
});

const InputAdornment = styled(Typography)({
	position: "absolute",
	right: "1rem",
	fontSize: "1rem",
	color: "#666666",
	pointerEvents: "none",
	zIndex: 1,
});

const ButtonContainer = styled(Box)(({ theme }) => ({
	display: "flex",
	gap: "1rem",
	marginTop: "1rem",
	[theme.breakpoints.down("sm")]: {
		flexDirection: "column",
	},
}));

const CalculateButton = styled(Button)(({ theme }) => ({
	backgroundColor: "#FC8019",
	color: "#FFFFFF",
	padding: "0.5rem 1rem",
	borderRadius: "0.75rem",
	textTransform: "none",
	fontSize: "1rem",
	fontWeight: "600",
	width: "auto",
	boxShadow: "none",
	"&:hover": {
		backgroundColor: "#FC8019",
		boxShadow: "none",
	},
	"&:disabled": {
		backgroundColor: "#E0E0E0",
		color: "#9E9E9E",
	},
}));

const ResetButton = styled(Button)(({ theme }) => ({
	backgroundColor: "transparent",
	color: "#FC8019",
	padding: "0.5rem 1rem",
	borderRadius: "0.75rem",
	textTransform: "none",
	fontSize: "1rem",
	fontWeight: "600",
	width: "auto",
	border: "2px solid #FC8019",
	boxShadow: "none",
	"&:hover": {
		backgroundColor: "transparent",
		border: "2px solid #FC8019",
		boxShadow: "none",
	},
}));

const Divider = styled(Box)(({ theme }) => ({
	width: "100%",
	height: "1px",
	backgroundColor: "#C4C4C4",
	margin: "1.5rem 0",
}));

const ResultContainer = styled(Box)(({ theme }) => ({
	backgroundColor: "#FFFFFF",
	borderRadius: "0.75rem",
	padding: "1.5rem",
	display: "flex",
	flexDirection: "column",
	gap: "1rem",
	marginTop: "1rem",
}));

const ResultRowContainer = styled(Box)(({ theme }) => ({
	display: "grid",
	gridTemplateColumns: "1fr 1fr 1fr",
	gap: "1rem",
	[theme.breakpoints.down("md")]: {
		gridTemplateColumns: "1fr",
	},
}));

const ResultFieldWrapper = styled(Box)({
	display: "flex",
	flexDirection: "column",
	gap: "0.5rem",
});

const ResultFieldLabel = styled(Typography)({
	fontSize: "0.875rem",
	fontWeight: "500",
	color: "#666666",
});

const DescriptionBox = styled(Box)(({ theme }) => ({
	backgroundColor: "#FAFAFA",
	borderRadius: "1rem",
	padding: "2rem 0rem 2rem 0rem",
	display: "flex",
	flexDirection: "column",
	gap: "1.5rem",
	[theme.breakpoints.down("md")]: {
		padding: "1.5rem",
	},
}));

const DescriptionText = styled(Typography)(({ theme }) => ({
	fontSize: "1rem",
	lineHeight: "1.75",
	color: "#333333",
	textAlign: "justify",
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
	fontSize: "1.5rem",
	fontWeight: "400",
	color: "#212121",
	marginTop: "1rem",
	[theme.breakpoints.down("md")]: {
		fontSize: "1.25rem",
	},
}));

const BulletList = styled("ul")(({ theme }) => ({
	marginTop: "0rem",
	marginBottom: "0.5rem",
	paddingLeft: "2rem",
	"& li": {
		fontSize: "1rem",
		lineHeight: "1.75",
		color: "#333333",
	},
}));

const AccordionContainer = styled(Box)(({ theme }) => ({
	backgroundColor: "#FAFAFA",
	borderRadius: "1rem",
	padding: "2rem 0rem",
	[theme.breakpoints.down("md")]: {
		padding: "1.5rem 0rem",
	},
}));

const AccordionWrapper = styled(Box)(({ theme }) => ({
	borderRadius: "0.5rem",
	border: "0.0625rem solid #C4C4C4",
	overflow: "hidden",
}));

const AccordionItem = styled(Box)(({ theme }) => ({
	borderBottom: "0.0625rem solid #C4C4C4",
	"&:last-child": {
		borderBottom: "none",
	},
}));

const AccordionHeader = styled(Box)(({ theme }) => ({
	display: "flex",
	justifyContent: "space-between",
	alignItems: "center",
	padding: "1rem 2rem",
	cursor: "pointer",
	backgroundColor: "#FFFFFF",
	"&:hover": {
		backgroundColor: "#F5F5F5",
	},
}));

const AccordionQuestion = styled(Typography)(({ theme }) => ({
	fontSize: "1rem",
	fontWeight: "500",
	color: "#212121",
	flex: 1,
}));

const AccordionIcon = styled(Box)(({ theme }) => ({
	width: "1.5rem",
	height: "1.5rem",
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	fontSize: "1.5rem",
	color: "#212121",
	fontWeight: "300",
	transition: "transform 0.1875s ease",
}));

const AccordionContent = styled(Box)(({ theme }) => ({
	padding: "0 2rem 1rem 2rem",
	fontSize: "1rem",
	lineHeight: "1.75",
	color: "#616161",
	backgroundColor: "#FFFFFF",
}));

const RentalYieldCalculator = () => {
	const router = useRouter();

	const [formData, setFormData] = useState({
		totalCost: "",
		monthlyRent: "",
		annualExpenses: "",
		vacancyRate: "",
		bankInterest: "No",
	});

	const [result, setResult] = useState<{
		grossRentalYield: number;
		netRentalYield: number;
		paybackPeriod: number;
	} | null>(null);
	const [openAccordion, setOpenAccordion] = React.useState<number | null>(0);

	const localLang = {
		mainTitle: "Rental Yield Calculator",
		subTitle: "Calculate your property's rental yield and payback period",
		totalCost: "Total Cost of Property",
		monthlyRent: "Expected Monthly Rent",
		annualExpenses: "Expected Annual Expenses",
		vacancyRate: "Expected Vacancy Rate",
		bankInterest: "Bank Interest on Rent?",
		calculate: "Calculate",
		reset: "Reset Data",
		grossRentalYield: "Gross Rental Yield",
		netRentalYield: "Net Rental Yield",
		paybackPeriod: "Payback Period",
		popularCalculators: "Popular Calculators",
		moreCalculators: "More Calculators",
		faqTitle: "Frequently Asked Questions",
	};

	const handleInputChange = (field: string, value: string) => {
		setFormData((prev) => ({
			...prev,
			[field]: value,
		}));
	};

	const handleSelectChange = (field: string) => (event: SelectChangeEvent<string>) => {
		setFormData((prev) => ({
			...prev,
			[field]: event.target.value,
		}));
	};

	const calculateRentalYield = () => {
		const totalCost = parseFloat(formData.totalCost);
		const monthlyRent = parseFloat(formData.monthlyRent);
		const annualExpenses = parseFloat(formData.annualExpenses) || 0;
		const vacancyRate = parseFloat(formData.vacancyRate) || 0;

		if (isNaN(totalCost) || isNaN(monthlyRent) || totalCost === 0) {
			return;
		}

		// Calculate annual rent
		const annualRent = monthlyRent * 12;

		// Adjust for vacancy rate
		const adjustedAnnualRent = annualRent * (1 - vacancyRate / 100);

		// Gross Rental Yield = (Annual Rent / Total Cost) × 100
		const grossRentalYield = (annualRent / totalCost) * 100;

		// Net Rental Yield = ((Annual Rent - Annual Expenses) / Total Cost) × 100
		const netRentalYield = ((adjustedAnnualRent - annualExpenses) / totalCost) * 100;

		// Payback Period = Total Cost / Net Annual Income
		const netAnnualIncome = adjustedAnnualRent - annualExpenses;
		const paybackPeriod = netAnnualIncome > 0 ? totalCost / netAnnualIncome : 0;

		setResult({
			grossRentalYield: Math.round(grossRentalYield * 100) / 100,
			netRentalYield: Math.round(netRentalYield * 100) / 100,
			paybackPeriod: Math.round(paybackPeriod * 100) / 100,
		});
	};

	const resetForm = () => {
		setFormData({
			totalCost: "",
			monthlyRent: "",
			annualExpenses: "",
			vacancyRate: "",
			bankInterest: "No",
		});
		setResult(null);
	};

	const isFormValid = () => {
		return formData.totalCost !== "" && formData.monthlyRent !== "";
	};

	const toggleAccordion = (index: number) => {
		setOpenAccordion((prev) => (prev === index ? null : index));
	};

	const faqData = [
		{
			question: "What is rental yield?",
			answer:
				"Rental yield is the annual rental income expressed as a percentage of the property's total cost. It helps measure the return on investment for rental properties.",
		},
		{
			question: "What is the difference between gross and net rental yield?",
			answer:
				"Gross rental yield considers only rental income, while net rental yield accounts for expenses like maintenance, taxes, and vacancy costs.",
		},
		{
			question: "What is a good rental yield?",
			answer:
				"A rental yield of 5-8% is generally considered good, though this varies by location and property type. Higher yields often indicate better returns.",
		},
		{
			question: "How is payback period calculated?",
			answer:
				"Payback period is the total property cost divided by net annual rental income. It shows how many years it takes to recover your investment through rent.",
		},
		{
			question: "Should I consider vacancy rate?",
			answer:
				"Yes, vacancy rate accounts for periods when the property may be unoccupied, giving you a more realistic estimate of actual rental income.",
		},
	];

	return (
		<CalculatorContainer>
			<MainContent>
				<HeaderBox>
					<MainTitle>{localLang.mainTitle}</MainTitle>
					<SubTitle>{localLang.subTitle}</SubTitle>
				</HeaderBox>
				<CalculatorBox>
					<InputRowContainer>
						<InputWrapper>
							<FieldLabel>{localLang.totalCost}</FieldLabel>
							<InputWithAdornment>
								<StyledTextField
									type="number"
									value={formData.totalCost}
									onChange={(e) => handleInputChange("totalCost", e.target.value)}
									placeholder=""
									fullWidth
									inputProps={{ min: 0 }}
									sx={{
										"& .MuiInputBase-input": {
											paddingLeft: "2.5rem",
										},
									}}
								/>
								<InputAdornment sx={{ left: "1rem", right: "auto" }}>₹</InputAdornment>
							</InputWithAdornment>
						</InputWrapper>
						<InputWrapper>
							<FieldLabel>{localLang.monthlyRent}</FieldLabel>
							<InputWithAdornment>
								<StyledTextField
									type="number"
									value={formData.monthlyRent}
									onChange={(e) => handleInputChange("monthlyRent", e.target.value)}
									placeholder=""
									fullWidth
									inputProps={{ min: 0 }}
									sx={{
										"& .MuiInputBase-input": {
											paddingLeft: "2.5rem",
										},
									}}
								/>
								<InputAdornment sx={{ left: "1rem", right: "auto" }}>₹</InputAdornment>
							</InputWithAdornment>
						</InputWrapper>
					</InputRowContainer>
					<InputRowContainer>
						<InputWrapper>
							<FieldLabel>{localLang.annualExpenses}</FieldLabel>
							<InputWithAdornment>
								<StyledTextField
									type="number"
									value={formData.annualExpenses}
									onChange={(e) => handleInputChange("annualExpenses", e.target.value)}
									placeholder=""
									fullWidth
									inputProps={{ min: 0 }}
									sx={{
										"& .MuiInputBase-input": {
											paddingLeft: "2.5rem",
										},
									}}
								/>
								<InputAdornment sx={{ left: "1rem", right: "auto" }}>₹</InputAdornment>
							</InputWithAdornment>
						</InputWrapper>
						<InputWrapper>
							<FieldLabel>{localLang.vacancyRate}</FieldLabel>
							<InputWithAdornment>
								<StyledTextField
									type="number"
									value={formData.vacancyRate}
									onChange={(e) => handleInputChange("vacancyRate", e.target.value)}
									placeholder=""
									fullWidth
									inputProps={{ min: 0, max: 100, step: 0.1 }}
									sx={{
										"& .MuiInputBase-input": {
											paddingRight: "2.5rem",
										},
									}}
								/>
								<InputAdornment>%</InputAdornment>
							</InputWithAdornment>
						</InputWrapper>
					</InputRowContainer>
					<InputRowContainer>
						<InputWrapper>
							<FieldLabel>{localLang.bankInterest}</FieldLabel>
							<StyledSelect
								value={formData.bankInterest}
								onChange={handleSelectChange("bankInterest")}
								fullWidth
							>
								<MenuItem value="Yes">Yes</MenuItem>
								<MenuItem value="No">No</MenuItem>
							</StyledSelect>
						</InputWrapper>
					</InputRowContainer>
					<ButtonContainer>
						<CalculateButton
							onClick={calculateRentalYield}
							disabled={!isFormValid()}
						>
							{localLang.calculate}
						</CalculateButton>
						<ResetButton onClick={resetForm}>{localLang.reset}</ResetButton>
					</ButtonContainer>
					{result !== null && (
						<>
							<Divider />
							<ResultContainer>
								<ResultRowContainer>
									<ResultFieldWrapper>
										<ResultFieldLabel>{localLang.grossRentalYield}</ResultFieldLabel>
										<InputWithAdornment>
											<StyledTextField
												value={result.grossRentalYield.toFixed(2)}
												fullWidth
												disabled
												sx={{
													"& .MuiInputBase-input": {
														color: "#212121",
														fontWeight: "700",
														fontSize: "1.25rem",
														WebkitTextFillColor: "#212121",
														paddingRight: "2.5rem",
													},
												}}
											/>
											<InputAdornment>%</InputAdornment>
										</InputWithAdornment>
									</ResultFieldWrapper>
									<ResultFieldWrapper>
										<ResultFieldLabel>{localLang.netRentalYield}</ResultFieldLabel>
										<InputWithAdornment>
											<StyledTextField
												value={result.netRentalYield.toFixed(2)}
												fullWidth
												disabled
												sx={{
													"& .MuiInputBase-input": {
														color: "#212121",
														fontWeight: "700",
														fontSize: "1.25rem",
														WebkitTextFillColor: "#212121",
														paddingRight: "2.5rem",
													},
												}}
											/>
											<InputAdornment>%</InputAdornment>
										</InputWithAdornment>
									</ResultFieldWrapper>
									<ResultFieldWrapper>
										<ResultFieldLabel>{localLang.paybackPeriod}</ResultFieldLabel>
										<InputWithAdornment>
											<StyledTextField
												value={result.paybackPeriod.toFixed(2)}
												fullWidth
												disabled
												sx={{
													"& .MuiInputBase-input": {
														color: "#212121",
														fontWeight: "700",
														fontSize: "1.25rem",
														WebkitTextFillColor: "#212121",
														paddingRight: "4rem",
													},
												}}
											/>
											<InputAdornment>Years</InputAdornment>
										</InputWithAdornment>
									</ResultFieldWrapper>
								</ResultRowContainer>
								<Typography
									sx={{
										fontSize: "0.875rem",
										color: "#757575",
									}}
								>
									*Calculator results are for reference only, Please verify with standard or official sources.
								</Typography>
							</ResultContainer>
						</>
					)}
				</CalculatorBox>
				<DescriptionBox>
					<DescriptionText>
						Rental Yield Calculator helps property investors quickly evaluate the profitability of rental properties.
						Simply enter your property cost, expected monthly rent, annual expenses, and vacancy rate to get instant
						insights on your investment returns.
					</DescriptionText>
					<DescriptionText>
						This tool calculates both gross and net rental yields, along with the payback period, helping you make
						informed investment decisions and compare different properties effectively.
					</DescriptionText>
					<SectionTitle>Understanding Rental Yield</SectionTitle>
					<DescriptionText>
						Rental yield is a key metric for property investors, showing the annual return on investment as a percentage
						of the property&apos;s total cost.
					</DescriptionText>
					<Box
						sx={{
							backgroundColor: "#E6E6E6",
							borderRadius: "0.5rem",
							padding: "1.5rem",
							textAlign: "center",
						}}
					>
						<Typography
							sx={{
								fontSize: "1rem",
								fontWeight: "500",
								color: "#9E9E9E",
								textDecoration: "underline",
								marginBottom: "0.75rem",
							}}
						>
							Gross Rental Yield Formula
						</Typography>
						<Typography
							sx={{
								fontSize: "1.125rem",
								fontWeight: "500",
								color: "#212121",
								marginBottom: "0.5rem",
							}}
						>
							Gross Rental Yield = (Annual Rent / Total Property Cost) × 100
						</Typography>
					</Box>
					<Box
						sx={{
							backgroundColor: "#E6E6E6",
							borderRadius: "0.5rem",
							padding: "1.5rem",
							textAlign: "center",
							marginTop: "1rem",
						}}
					>
						<Typography
							sx={{
								fontSize: "1rem",
								fontWeight: "500",
								color: "#9E9E9E",
								textDecoration: "underline",
								marginBottom: "0.75rem",
							}}
						>
							Net Rental Yield Formula
						</Typography>
						<Typography
							sx={{
								fontSize: "1.125rem",
								fontWeight: "500",
								color: "#212121",
								marginBottom: "0.5rem",
							}}
						>
							Net Rental Yield = ((Annual Rent - Annual Expenses) / Total Property Cost) × 100
						</Typography>
					</Box>

					<SectionTitle>Key Components</SectionTitle>
					<BulletList>
						<li>
							<strong>Total Property Cost:</strong> The complete purchase price including all buying costs
						</li>
						<li>
							<strong>Monthly Rent:</strong> Expected rental income per month
						</li>
						<li>
							<strong>Annual Expenses:</strong> Maintenance, taxes, insurance, and other yearly costs
						</li>
						<li>
							<strong>Vacancy Rate:</strong> Percentage of time the property might remain unoccupied
						</li>
						<li>
							<strong>Payback Period:</strong> Years needed to recover your investment through rental income
						</li>
					</BulletList>

					<SectionTitle>Why Use This Calculator</SectionTitle>
					<DescriptionText>
						Understanding rental yield helps you compare investment opportunities, set competitive rental prices, and
						plan your long-term financial strategy. A higher yield generally indicates a better return on your
						investment.
					</DescriptionText>
				</DescriptionBox>
				<Box>
					<SectionTitle>{localLang.faqTitle}</SectionTitle>
					<AccordionContainer>
						<AccordionWrapper>
							{faqData.map((faq, index) => (
								<AccordionItem key={index}>
									<AccordionHeader onClick={() => toggleAccordion(index)}>
										<AccordionQuestion>{faq.question}</AccordionQuestion>
										<AccordionIcon>{openAccordion === index ? "−" : "+"}</AccordionIcon>
									</AccordionHeader>
									{openAccordion === index && <AccordionContent>{faq.answer}</AccordionContent>}
								</AccordionItem>
							))}
						</AccordionWrapper>
					</AccordionContainer>
				</Box>
			</MainContent>

			<Sidebar>
				{/* AI Boost Card */}
				<AiBoostCard href={"/loading.html?callbackUrl=" + encodeURIComponent("/" + router.locale + "/ai-boost")}>
					<Box
						sx={{
							position: "absolute",
							top: "1rem",
							left: "50%",
							transform: "translateX(-50%)",
							width: "120px",
							height: "120px",
						}}
					>
						<img
							src="/images/icons/ai-boost/rocket-new.png"
							alt="AI Boost Rocket"
							style={{
								width: "100%",
								height: "100%",
								objectFit: "contain",
							}}
						/>
					</Box>
					<AiBoostTitle>AI Boost</AiBoostTitle>
					<AiBoostDescription>
						Let pre-qualified leads come to you with AI-powered, automated campaigns
					</AiBoostDescription>
					<AiBoostButton>Try Boost Now</AiBoostButton>
				</AiBoostCard>

				<SidebarCard>
					<SidebarCardHeader>
						<SidebarCardTitle>{localLang.popularCalculators}</SidebarCardTitle>
					</SidebarCardHeader>
					<SidebarCardContent>
						<CalculatorLink
							onClick={() =>
								router.push(
									"/loading.html?callbackUrl=" + encodeURIComponent("/" + router.locale + "/calculators/katha-sqft"),
								)
							}
						>
							Katha to Square feet
						</CalculatorLink>
						<CalculatorLink
							onClick={() =>
								router.push(
									"/loading.html?callbackUrl=" + encodeURIComponent("/" + router.locale + "/calculators/sqmt-sqyd"),
								)
							}
						>
							Square Meter to Square Yard
						</CalculatorLink>
						<CalculatorLink
							onClick={() =>
								router.push(
									"/loading.html?callbackUrl=" + encodeURIComponent("/" + router.locale + "/calculators/hectare-bigha"),
								)
							}
						>
							Hectare to Bigha
						</CalculatorLink>
						<CalculatorLink
							onClick={() =>
								router.push(
									"/loading.html?callbackUrl=" + encodeURIComponent("/" + router.locale + "/calculators/cent-sqft"),
								)
							}
						>
							Cent to Square Feet
						</CalculatorLink>
						<CalculatorLink
							onClick={() =>
								router.push(
									"/loading.html?callbackUrl=" + encodeURIComponent("/" + router.locale + "/calculators/acre-sqft"),
								)
							}
						>
							Acre to Square Feet
						</CalculatorLink>
						<CalculatorLink
							onClick={() =>
								router.push(
									"/loading.html?callbackUrl=" + encodeURIComponent("/" + router.locale + "/calculators/sqft-acre"),
								)
							}
						>
							Square Feet to Acre
						</CalculatorLink>
						<CalculatorLink
							onClick={() =>
								router.push(
									"/loading.html?callbackUrl=" + encodeURIComponent("/" + router.locale + "/calculators/bigha-acre"),
								)
							}
						>
							Bigha to Acre
						</CalculatorLink>
						<CalculatorLink
							onClick={() =>
								router.push(
									"/loading.html?callbackUrl=" +
										encodeURIComponent("/" + router.locale + "/calculators/home-affordability-calculator"),
								)
							}
						>
							Home Affordability Calculator
						</CalculatorLink>
						<CalculatorLink
							onClick={() =>
								router.push(
									"/loading.html?callbackUrl=" +
										encodeURIComponent("/" + router.locale + "/calculators/property-tax-calculator"),
								)
							}
						>
							Property Tax Calculator
						</CalculatorLink>
					</SidebarCardContent>
				</SidebarCard>

				<SidebarCard>
					<SidebarCardHeader>
						<SidebarCardTitle>{localLang.moreCalculators}</SidebarCardTitle>
					</SidebarCardHeader>
					<SidebarCardContent>
						<CalculatorLink
							onClick={() =>
								router.push(
									"/loading.html?callbackUrl=" + encodeURIComponent("/" + router.locale + "/calculators/acre-sqmt"),
								)
							}
						>
							Acre to Square Meter
						</CalculatorLink>
						<CalculatorLink
							onClick={() =>
								router.push(
									"/loading.html?callbackUrl=" + encodeURIComponent("/" + router.locale + "/calculators/acre-hectare"),
								)
							}
						>
							Acre to Hectare
						</CalculatorLink>
						<CalculatorLink
							onClick={() =>
								router.push(
									"/loading.html?callbackUrl=" +
										encodeURIComponent("/" + router.locale + "/calculators/rental-yield-calculator"),
								)
							}
						>
							Rental Yield Calculator
						</CalculatorLink>
						<CalculatorLink
							onClick={() =>
								router.push(
									"/loading.html?callbackUrl=" +
										encodeURIComponent("/" + router.locale + "/calculators/stamp-duty-calculator"),
								)
							}
						>
							Stamp Duty Calculator
						</CalculatorLink>
						<CalculatorLink
							onClick={() =>
								router.push(
									"/loading.html?callbackUrl=" +
										encodeURIComponent("/" + router.locale + "/calculators/mortgage-emi-calculator"),
								)
							}
						>
							Mortgage EMI Calculator
						</CalculatorLink>
						<CalculatorLink
							onClick={() =>
								router.push(
									"/loading.html?callbackUrl=" +
										encodeURIComponent("/" + router.locale + "/calculators/home-insurance-premium-estimator"),
								)
							}
						>
							Home Insurance Premium Estimator
						</CalculatorLink>
					</SidebarCardContent>
				</SidebarCard>
			</Sidebar>
		</CalculatorContainer>
	);
};

export default RentalYieldCalculator;
